import { Trash2 } from "lucide-react";
import { formatPriceCents } from "@/components/services/service-meta";
import { cn } from "@/lib/cn";
import { formatarQuantidade } from "@/lib/quantidade";

const inputCls =
  "h-8 w-full rounded border border-zinc-200 bg-white px-2 text-right text-xs tabular-nums text-zinc-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-zinc-50 disabled:text-zinc-400 dark:border-border dark:bg-card dark:text-foreground";

export type ConsumoLinha = {
  id: string;
  materialLineId: string | null;
  rotulo: string;
  unidade: string | null;
  quantityNet: number;
  correctionFactor: number;
  unitCostCents: number | null;
  indirect: boolean;
  deRetrabalho: boolean;
  opcoes: { id: string; name: string; purchasePriceCents: number | null }[];
  quantidadeBruta: number;
  custoCents: number | null;
  emAberto: boolean;
};

const paraNumero = (v: string) => Number((v || "0").replace(",", "."));
const paraTexto = (n: number) => String(n).replace(".", ",");

/**
 * Uma linha do que o serviço consumiu.
 *
 * A linha em aberto ("qualquer dissilicato") vira um seletor em âmbar: é a única pendência que
 * impede o custo de fechar, e precisa se distinguir das outras à primeira vista.
 *
 * Quantidade e fator são editáveis porque a ficha é previsão e isto é gasto — quem quebrou um bloco
 * registra dois.
 */
export function LinhaDeConsumo({
  linha,
  podeEditar,
  podeVerCusto,
  onQuantidade,
  onFator,
  onResolver,
  onRemover,
}: {
  linha: ConsumoLinha;
  podeEditar: boolean;
  podeVerCusto: boolean;
  onQuantidade: (v: number) => void;
  onFator: (v: number) => void;
  onResolver: (materialLineId: string) => void;
  onRemover: () => void;
}) {
  return (
    <tr
      className={cn(
        "border-b border-zinc-100 last:border-b-0 dark:border-border/50",
        linha.emAberto && "bg-amber-50/60 dark:bg-amber-950/10",
      )}
    >
      <td className="py-1.5 pr-2">
        <span className="block truncate text-xs text-zinc-900 dark:text-foreground">{linha.rotulo}</span>
        <span className="flex flex-wrap gap-1.5 text-[10px] text-zinc-400 dark:text-muted-foreground/70">
          {linha.deRetrabalho && (
            <span className="font-semibold text-zinc-500 dark:text-muted-foreground">Retrabalho</span>
          )}
          {linha.indirect && <span>Indireto</span>}
          {podeVerCusto && linha.unitCostCents != null && !linha.emAberto && (
            <span>
              {formatPriceCents(linha.unitCostCents)}
              {linha.unidade ? `/${linha.unidade}` : ""}
            </span>
          )}
          {podeVerCusto && linha.unitCostCents == null && !linha.emAberto && !linha.indirect && (
            <span className="text-amber-600 dark:text-amber-400">Sem preço</span>
          )}
        </span>

        {linha.emAberto && (
          <select
            value=""
            disabled={!podeEditar}
            onChange={(e) => e.target.value && onResolver(e.target.value)}
            aria-label={`Escolher insumo de ${linha.rotulo}`}
            className="mt-1 h-7 w-full rounded border border-amber-300 bg-white px-1.5 text-[11px] text-amber-800 disabled:opacity-60 dark:border-amber-500/40 dark:bg-card dark:text-amber-300"
          >
            <option value="">Escolher o que foi usado…</option>
            {linha.opcoes.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
                {podeVerCusto && o.purchasePriceCents != null ? ` — ${formatPriceCents(o.purchasePriceCents)}` : ""}
              </option>
            ))}
          </select>
        )}
      </td>

      <td className="py-1.5 pr-1.5">
        <input
          defaultValue={paraTexto(linha.quantityNet)}
          key={`q-${linha.id}-${linha.quantityNet}`}
          disabled={!podeEditar}
          onBlur={(e) => {
            const v = paraNumero(e.target.value);
            if (v > 0 && v !== linha.quantityNet) onQuantidade(v);
            else e.target.value = paraTexto(linha.quantityNet);
          }}
          inputMode="decimal"
          aria-label={`Quantidade de ${linha.rotulo}`}
          className={inputCls}
        />
      </td>

      <td className="py-1.5 pr-1.5">
        <input
          defaultValue={paraTexto(linha.correctionFactor)}
          key={`f-${linha.id}-${linha.correctionFactor}`}
          disabled={!podeEditar}
          onBlur={(e) => {
            const v = paraNumero(e.target.value);
            if (v >= 1 && v !== linha.correctionFactor) onFator(v);
            else e.target.value = paraTexto(linha.correctionFactor);
          }}
          inputMode="decimal"
          aria-label={`Fator de perda de ${linha.rotulo}`}
          className={inputCls}
        />
      </td>

      <td className="py-1.5 pr-2 text-right text-[11px] tabular-nums text-zinc-500 dark:text-muted-foreground">
        {linha.quantidadeBruta > 0 ? formatarQuantidade(linha.quantidadeBruta) : "—"}
      </td>

      {podeVerCusto && (
        <td
          className={cn(
            "py-1.5 pr-2 text-right text-xs tabular-nums",
            linha.indirect
              ? "text-zinc-400 line-through dark:text-muted-foreground/60"
              : "text-zinc-900 dark:text-foreground",
          )}
        >
          {linha.custoCents != null ? formatPriceCents(linha.custoCents) : "—"}
        </td>
      )}

      <td className="py-1.5 text-right">
        {podeEditar && (
          <button
            type="button"
            onClick={onRemover}
            aria-label={`Remover ${linha.rotulo}`}
            className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:text-red-400"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </td>
    </tr>
  );
}
